import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { useNotification } from './NotificationSystem';
import './Friends.css';

function Friends({ token, user, socket, onStartDM }) {
  const { showSuccess, showError, confirm } = useNotification();
  const [activeTab, setActiveTab] = useState('online'); // online, all, pending, blocked
  const [friends, setFriends] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddFriend, setShowAddFriend] = useState(false);
  const [friendUsername, setFriendUsername] = useState('');
  const [addFriendError, setAddFriendError] = useState('');
  const [addFriendSuccess, setAddFriendSuccess] = useState('');
  const [addingFriend, setAddingFriend] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
    
    // Listen for socket events
    if (socket) {
      socket.on('friend_request_received', handleFriendRequestReceived);
      socket.on('friend_added', handleFriendAdded);
      socket.on('friend_removed', handleFriendRemoved);
      
      return () => {
        socket.off('friend_request_received', handleFriendRequestReceived);
        socket.off('friend_added', handleFriendAdded);
        socket.off('friend_removed', handleFriendRemoved);
      };
    }
  }, [socket]);

  const handleFriendRequestReceived = (data) => {
    setPendingRequests(prev => [...prev, data]);
  };

  const handleFriendAdded = (friend) => {
    setFriends(prev => [...prev, friend]);
    setPendingRequests(prev => prev.filter(r => r.from_user_id !== friend.id));
  };

  const handleFriendRemoved = ({ friendId }) => {
    setFriends(prev => prev.filter(f => f.id !== friendId));
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [friendsRes, requestsRes, blockedRes] = await Promise.all([
        fetch(`${API_URL}/api/friends`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/friends/requests`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/users/blocked`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (friendsRes.ok) setFriends(await friendsRes.json());
      if (requestsRes.ok) setPendingRequests(await requestsRes.json());
      if (blockedRes.ok) setBlockedUsers(await blockedRes.json());
    } catch (error) {
      console.error('Error loading friends data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFriend = async (e) => {
    e.preventDefault();
    setAddFriendError('');
    setAddFriendSuccess('');
    setAddingFriend(true);

    try {
      const response = await fetch(`${API_URL}/api/friends/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ username: friendUsername })
      });

      const data = await response.json();

      if (response.ok) {
        setAddFriendSuccess(data.message);
        setFriendUsername('');
        setTimeout(() => {
          setShowAddFriend(false);
          setAddFriendSuccess('');
        }, 2000);
        
        if (data.friend) {
          // Auto-accepted (they sent us a request first)
          setFriends(prev => [...prev, data.friend]);
        }
      } else {
        setAddFriendError(data.error);
      }
    } catch (error) {
      setAddFriendError('Failed to send friend request');
    } finally {
      setAddingFriend(false);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      const response = await fetch(`${API_URL}/api/friends/accept/${requestId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setFriends(prev => [...prev, data.friend]);
        setPendingRequests(prev => prev.filter(r => r.id !== requestId));
      }
    } catch (error) {
      console.error('Error accepting friend request:', error);
    }
  };

  const handleDeclineRequest = async (requestId) => {
    try {
      const response = await fetch(`${API_URL}/api/friends/decline/${requestId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setPendingRequests(prev => prev.filter(r => r.id !== requestId));
      }
    } catch (error) {
      console.error('Error declining friend request:', error);
    }
  };

  const handleRemoveFriend = async (friendId) => {
    if (!await confirm('Remove this friend?', 'Remove Friend')) return;

    try {
      const response = await fetch(`${API_URL}/api/friends/${friendId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setFriends(prev => prev.filter(f => f.id !== friendId));
      }
    } catch (error) {
      console.error('Error removing friend:', error);
    }
  };

  const handleBlockUser = async (userId) => {
    if (!await confirm('Block this user?', 'Block User')) return;

    try {
      const response = await fetch(`${API_URL}/api/users/block`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ userId })
      });

      if (response.ok) {
        setFriends(prev => prev.filter(f => f.id !== userId));
        loadData(); // Reload to update blocked list
      }
    } catch (error) {
      console.error('Error blocking user:', error);
    }
  };

  const handleUnblockUser = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/users/unblock/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setBlockedUsers(prev => prev.filter(u => u.id !== userId));
      }
    } catch (error) {
      console.error('Error unblocking user:', error);
    }
  };

  if (loading) {
    return (
      <div className="friends-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  // Filter friends based on search query
  const filteredFriends = searchQuery
    ? friends.filter(friend => 
        friend.username.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : friends;

  // Filter online friends
  const onlineFriends = filteredFriends.filter(friend => friend.status === 'online');

  return (
    <div className="friends-container">
      <div className="friends-header">
        <div className="friends-tabs">
          <button 
            className={`friends-tab ${activeTab === 'online' ? 'active' : ''}`}
            onClick={() => setActiveTab('online')}
          >
            Online {onlineFriends.length > 0 && <span className="tab-count">{onlineFriends.length}</span>}
          </button>
          <button 
            className={`friends-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All {friends.length > 0 && <span className="tab-count">{friends.length}</span>}
          </button>
          <button 
            className={`friends-tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            Pending {pendingRequests.length > 0 && <span className="tab-count">{pendingRequests.length}</span>}
          </button>
          <button 
            className={`friends-tab ${activeTab === 'blocked' ? 'active' : ''}`}
            onClick={() => setActiveTab('blocked')}
          >
            Blocked {blockedUsers.length > 0 && <span className="tab-count">{blockedUsers.length}</span>}
          </button>
        </div>
        <div className="friends-header-right">
          <input 
            type="text"
            className="friends-search"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button className="add-friend-button" onClick={() => setShowAddFriend(!showAddFriend)}>
            Add Friend
          </button>
        </div>
      </div>

      {showAddFriend && (
        <div className="add-friend-form">
          <form onSubmit={handleAddFriend}>
            <input
              type="text"
              value={friendUsername}
              onChange={(e) => setFriendUsername(e.target.value)}
              placeholder="Enter username"
              disabled={addingFriend}
            />
            <button type="submit" disabled={addingFriend || !friendUsername}>
              {addingFriend ? 'Sending...' : 'Send Request'}
            </button>
          </form>
          {addFriendError && <div className="add-friend-error">{addFriendError}</div>}
          {addFriendSuccess && <div className="add-friend-success">{addFriendSuccess}</div>}
        </div>
      )}

      <div className="friends-list">
        {activeTab === 'online' && (
          <>
            {onlineFriends.length === 0 ? (
              <div className="empty-state">
                <p>No friends are currently online</p>
              </div>
            ) : (
              onlineFriends.map(friend => (
                <div key={friend.id} className="friend-item">
                  <div className="friend-avatar online">
                    {friend.avatar_url ? (
                      <img src={`${API_URL}${friend.avatar_url}`} alt={friend.username} />
                    ) : (
                      friend.username.substring(0, 1).toUpperCase()
                    )}
                  </div>
                  <div className="friend-info">
                    <div className="friend-name">{friend.username}</div>
                    <div className="friend-status online">Online</div>
                  </div>
                  <div className="friend-actions">
                    <button 
                      className="friend-action-button message"
                      onClick={() => onStartDM(friend.id)}
                      title="Message"
                    >
                      💬
                    </button>
                    <button 
                      className="friend-action-button remove"
                      onClick={() => handleRemoveFriend(friend.id)}
                      title="Remove Friend"
                    >
                      ✕
                    </button>
                    <button 
                      className="friend-action-button block"
                      onClick={() => handleBlockUser(friend.id)}
                      title="Block"
                    >
                      🚫
                    </button>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {activeTab === 'all' && (
          <>
            {filteredFriends.length === 0 ? (
              <div className="empty-state">
                <p>{searchQuery ? 'No friends found' : 'No friends yet. Add some friends to get started!'}</p>
              </div>
            ) : (
              filteredFriends.map(friend => (
                <div key={friend.id} className="friend-item">
                  <div className="friend-avatar">
                    {friend.avatar_url ? (
                      <img src={`${API_URL}${friend.avatar_url}`} alt={friend.username} />
                    ) : (
                      friend.username.substring(0, 1).toUpperCase()
                    )}
                  </div>
                  <div className="friend-info">
                    <div className="friend-name">{friend.username}</div>
                    <div className="friend-status">{friend.status || 'offline'}</div>
                  </div>
                  <div className="friend-actions">
                    <button 
                      className="friend-action-button message"
                      onClick={() => onStartDM(friend.id)}
                      title="Message"
                    >
                      💬
                    </button>
                    <button 
                      className="friend-action-button remove"
                      onClick={() => handleRemoveFriend(friend.id)}
                      title="Remove Friend"
                    >
                      ✕
                    </button>
                    <button 
                      className="friend-action-button block"
                      onClick={() => handleBlockUser(friend.id)}
                      title="Block"
                    >
                      🚫
                    </button>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {activeTab === 'pending' && (
          <>
            {pendingRequests.length === 0 ? (
              <div className="empty-state">
                <p>No pending friend requests</p>
              </div>
            ) : (
              pendingRequests.map(request => (
                <div key={request.id} className="friend-item pending">
                  <div className="friend-avatar">
                    {request.from_avatar_url ? (
                      <img src={`${API_URL}${request.from_avatar_url}`} alt={request.from_username} />
                    ) : (
                      request.from_username.substring(0, 1).toUpperCase()
                    )}
                  </div>
                  <div className="friend-info">
                    <div className="friend-name">{request.from_username}</div>
                    <div className="friend-status">Incoming friend request</div>
                  </div>
                  <div className="friend-actions">
                    <button 
                      className="friend-action-button accept"
                      onClick={() => handleAcceptRequest(request.id)}
                    >
                      ✓ Accept
                    </button>
                    <button 
                      className="friend-action-button decline"
                      onClick={() => handleDeclineRequest(request.id)}
                    >
                      ✕ Decline
                    </button>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {activeTab === 'blocked' && (
          <>
            {blockedUsers.length === 0 ? (
              <div className="empty-state">
                <p>No blocked users</p>
              </div>
            ) : (
              blockedUsers.map(user => (
                <div key={user.id} className="friend-item blocked">
                  <div className="friend-avatar">
                    {user.avatar_url ? (
                      <img src={`${API_URL}${user.avatar_url}`} alt={user.username} />
                    ) : (
                      user.username.substring(0, 1).toUpperCase()
                    )}
                  </div>
                  <div className="friend-info">
                    <div className="friend-name">{user.username}</div>
                    <div className="friend-status">Blocked</div>
                  </div>
                  <div className="friend-actions">
                    <button 
                      className="friend-action-button unblock"
                      onClick={() => handleUnblockUser(user.id)}
                    >
                      Unblock
                    </button>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Friends;
